import { Injectable } from 'framework/di';

import type { CartResponse } from '../../providers/shopify';
import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { PlatformLoader } from '../platform/platform.loader';

/**
 * Interface for cart item to be added
 */
export interface CartAddItem {
  id: string | number;
  quantity: number;
  selling_plan?: string | null;
  properties?: Record<string, unknown>;
}

/**
 * Interface for cart change response
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CartChangeResponse extends CartResponse {
  // Extends base CartResponse with any change-specific fields
}

@Injectable
export class CartAPI extends BoostAPI {
  constructor(
    platformLoader: PlatformLoader,
    private appService: AppService
  ) {
    super(platformLoader);
  }
  /**
   * Get request headers for cart API calls
   * Override to add custom headers (tracking, authentication, etc.)
   *
   * @example
   * ```typescript
   * class CustomCartAPI extends CartAPI {
   *   protected getRequestHeaders(): HeadersInit {
   *     return {
   *       ...super.getRequestHeaders(),
   *       'X-Shop-ID': this.getShopId(),
   *       'X-Session-ID': this.getSessionId(),
   *     };
   *   }
   * }
   * ```
   */
  protected getRequestHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
  }

  /**
   * Get retry configuration for cart API operations
   * Override to customize retry behavior for high-traffic shops
   */
  protected getRetryConfig(): { maxAttempts: number; baseDelay: number } {
    return {
      maxAttempts: 3,
      baseDelay: 100,
    };
  }

  /**
   * Get all cart data from Shopify
   *
   * @returns Promise resolving to complete cart data including items, totals, and metadata
   * @throws Error if cart fetch fails
   */
  async getAll(): Promise<CartResponse> {
    const response = await fetch(this.buildEndpointWithLocale('cart.js'), {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get array of items currently in cart
   *
   * @returns Promise resolving to array of cart items (empty array if cart is empty)
   */
  async getProductsInCart() {
    const cartData = await this.getAll();
    return cartData.items || [];
  }

  /**
   * Process item properties before adding to cart
   * Override to add shop-specific properties or modify default behavior
   *
   * @param properties - Base properties for the line item
   * @param sellingPlanId - Selling plan ID if applicable
   * @returns Processed properties with any additional fields
   *
   * @example
   * ```typescript
   * class CustomCartAPI extends CartAPI {
   *   protected processItemProperties(properties, sellingPlanId) {
   *     const processed = super.processItemProperties(properties, sellingPlanId);
   *     return {
   *       ...processed,
   *       _shopSource: 'custom-widget',
   *       _timestamp: Date.now(),
   *     };
   *   }
   * }
   * ```
   */
  protected processItemProperties(
    properties: Record<string, unknown>,
    sellingPlanId: string | null
  ): Record<string, unknown> {
    return sellingPlanId ? { ...properties, _boostPreOrder: true } : properties;
  }

  /**
   * Add single product to cart
   *
   * @param id - Product variant ID
   * @param quantity - Quantity to add (default: 1)
   * @param sellingPlanId - Optional selling plan ID for subscriptions
   * @param properties - Optional custom properties for line item
   * @returns Promise resolving to Response object
   * @throws Error if cart addition fails
   *
   * @example
   * ```typescript
   * // Add regular product
   * await cartAPI.add('12345', 2);
   *
   * // Add with subscription
   * await cartAPI.add('12345', 1, 'selling_plan_123');
   *
   * // Add with custom properties
   * await cartAPI.add('12345', 1, null, { gift_message: 'Happy Birthday!' });
   * ```
   */
  async add(
    id: string | number,
    quantity = 1,
    sellingPlanId: string | null = null,
    properties: Record<string, unknown> = {}
  ) {
    const payload = {
      items: [
        {
          id,
          quantity,
          selling_plan: sellingPlanId,
          properties: this.processItemProperties(properties, sellingPlanId),
        },
      ],
    };

    const response = await fetch(this.buildEndpointWithLocale('cart/add.js'), {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: this.getRequestHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add to cart: ${response.status} - ${errorText}`);
    }

    return response;
  }

  /**
   * Add multiple products to cart in a single request
   *
   * @param data - Array of product items to add
   * @returns Promise resolving to Response object
   * @throws Error if cart addition fails
   *
   * @example
   * ```typescript
   * await cartAPI.addMultiProductToCart([
   *   { id: '12345', quantity: 2 },
   *   { id: '67890', quantity: 1, properties: { engraving: 'John' } }
   * ]);
   * ```
   */
  async addMultiProductToCart(data: CartAddItem[]) {
    const payload = {
      items: data,
    };

    const response = await fetch(this.buildEndpointWithLocale('cart/add.js'), {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: this.getRequestHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add multiple products to cart: ${response.status} - ${errorText}`);
    }

    return response;
  }

  /**
   * Change cart item quantity with automatic retry on conflicts
   * Uses exponential backoff strategy for 409 Conflict errors
   *
   * @param id - Product variant ID or cart line item ID
   * @param quantity - New quantity (0 to remove item)
   * @param retryCount - Internal retry counter (do not set manually)
   * @returns Promise resolving to updated cart data
   * @throws Error if update fails after all retries
   *
   * @example
   * ```typescript
   * await cartAPI.change('12345', 2); // Set quantity to 2
   * await cartAPI.change('12345', 0); // Remove item
   * ```
   */
  async change(id: string | number, quantity = 0, retryCount = 0): Promise<CartChangeResponse> {
    const payload = {
      id,
      quantity,
    };

    const response = await fetch(this.buildEndpointWithLocale('cart/change.js'), {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: this.getRequestHeaders(),
    });

    const retryConfig = this.getRetryConfig();
    if (response.status === 409 && retryCount < retryConfig.maxAttempts) {
      this.appService.logger.warn(
        `Cart conflict (409), retrying... (attempt ${retryCount + 1}/${retryConfig.maxAttempts})`
      );
      await new Promise((resolve) => setTimeout(resolve, retryConfig.baseDelay * (retryCount + 1)));
      return this.change(id, quantity, retryCount + 1);
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Cart update failed: ${response.status} - ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Clear all items from cart
   *
   * @returns Promise resolving to empty cart data
   * @throws Error if cart clear operation fails
   *
   * @example
   * ```typescript
   * await cartAPI.clearCart();
   * ```
   */
  async clearCart(): Promise<CartResponse> {
    const response = await fetch(this.buildEndpointWithLocale('cart/clear.js'), { method: 'POST' });

    if (!response.ok) {
      throw new Error(`Failed to clear cart: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
  }
}
